import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import css from './CampaignStatsDetail.module.css';
import {
  Alert,
  Box,
  BoxContent,
  BoxHeading,
  Button,
  Chart,
  ChartData,
  Heading,
  Spinner,
} from '@mm/components';
import { useRouteParams } from '../../../../beta/hooks/use-route-params';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import {
  CampaignSchedulerAttribute,
  PlatformType,
} from '@mm/state/model/CampaignsModel';
import moment from 'moment';
import { get } from 'lodash';
import { useHistory, useLocation } from 'react-router';
import TableGrid, {
  Props as TableProps,
} from '../../../../../../common/components/TableGrid/TableGrid';
import * as queryString from 'query-string';
import { colors } from '../../../../beta/styles/colors';
import { GridCell } from '../../../../../../common/components/Grid/Grid';
import { AngleLeftLightIcon } from '@mm/icons/general';
import { useSelector } from 'react-redux';
import {
  CampaignTypeWithMappedPurposeAndDisplayStatus,
  selectCampaignById,
} from '@mm/state/selectors/campaignSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import {
  getDatesBack,
  getDatesFrom,
  getPlatformTitle,
  getTitle,
  keys,
  StatsKey,
  StatsType,
} from '../stats-helpers';
import { getTotal } from '../CampaignStatsCompact/CampaignStatsCompact';

type Props = {
  className?: string;
};

export const rangeStats = (
  stats?: StatsType,
  createdAt?: CampaignTypeWithMappedPurposeAndDisplayStatus['createdAt'],
) => {
  const dates = createdAt ? getDatesFrom(createdAt) : getDatesBack(30);

  return dates.map(v => {
    const stat = stats?.find?.(({ date }) => v.isSame(moment(date)));
    const date = v.format('MM/DD/YYYY');

    return stat
      ? { ...stat, date, id: date }
      : { id: date, date, statuses: {} };
  });
};

export const mapToChart = (
  key: StatsKey,
  platform: PlatformType,
  stats?: StatsType,
) => {
  return {
    data: [stats?.map?.(stat => stat?.statuses?.[key]?.[platform] || 0)],
    color: [colors.bluePurple],
    labels: stats?.map?.(stat => stat?.date),
  };
};

const renderCell = (item, key) => {
  const val = get(item, key) || 0;
  return <GridCell>{val}</GridCell>;
};

export const CampaignStatsDetail = ({ className }: Props) => {
  const { campaignId, schedulerId } = useRouteParams<{
    campaignId?: string;
    schedulerId?: string;
  }>();

  const campaign = useSelector<
    StoreState,
    CampaignTypeWithMappedPurposeAndDisplayStatus | undefined
  >(state => selectCampaignById(state, Number(campaignId)));

  const [state, setState] = useState<'loading' | 'error' | 'success'>();
  const [stats, setStats] = useState<StatsType>();

  const campaignCRUDActions = useCampaignCRUDActions();

  const location = useLocation();
  const history = useHistory();

  const params = useRouteParams();

  const { search } = location;
  const sp = queryString.parse(search);
  const key = sp.key as string;
  const platform = sp.platform as string;

  const isActive = useCallback(
    v => {
      return search?.includes(v);
    },
    [search],
  );

  const handleClick = useCallback(
    key => {
      history.push({
        ...location,
        search: queryString.stringify({ ...sp, ...{ key } }),
      });
    },
    [params, location],
  );

  const goBack = useCallback(
    key => {
      history.push(location.pathname.split('/stats')[0]);
    },
    [location.pathname],
  );

  const fetchStats = async () => {
    if (campaignId && schedulerId && state !== 'loading') {
      setState('loading');
      try {
        const res = await campaignCRUDActions.retrieveCampaignSchedulerStats(
          Number(campaignId),
          Number(schedulerId),
        );

        const stats = res?.stats?.[schedulerId];

        if (stats) {
          setStats(stats);
        }

        setState('success');
      } catch (err) {
        setState('error');
      }
    }
  };

  const rangedStats = useMemo(
    () => rangeStats(stats, campaign?.createdAt),
    [stats, campaign?.createdAt, key],
  );

  const chartData = useMemo(
    () => mapToChart(key, platform, rangedStats),
    [key, platform, rangedStats],
  );

  const navs = useMemo(() => {
    const scheduler = campaign?.schedulersAttributes?.find(
      v => v.id === schedulerId,
    );

    return keys
      .filter(key => key !== 'queued')
      .filter(key => {
        const total = getTotal(
          key,
          platform,
          (scheduler || {}) as CampaignSchedulerAttribute,
        );
        return total !== '-';
      });
  }, [campaign, platform, keys]);

  const tableItems = useMemo(() => rangedStats.reverse(), [rangedStats, key]);

  const tableData = useMemo(
    () => [
      {
        label: 'Date',
        id: 'date',
        keyName: 'date',
        renderCell,
      },
      {
        label: getTitle(key, platform),
        id: 'value',
        keyName: `statuses.${key}.${platform}`,
        renderCell,
      },
    ],
    [key, platform, stats],
  );

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div className={classNames(className, css.container)}>
      <Box className={css.card}>
        {state === 'error' && (
          <Alert className={css.error} showCloseIcon={false}>
            Couldn't load statistic
          </Alert>
        )}

        {state === 'success' && (
          <>
            <BoxHeading className={css.header}>
              {getPlatformTitle(platform)}
            </BoxHeading>
            <BoxContent className={css.content}>
              <div className={css.nav}>
                <Button
                  color="transparent-no-shadow"
                  css={{ container: css.button }}
                  leftIcon={<AngleLeftLightIcon color={colors.accentBlue} />}
                  onClick={goBack}
                >
                  Back to Dialogue
                </Button>
              </div>

              <div className={css.topbar}>
                {navs.map(v => (
                  <div
                    key={v}
                    className={classNames(css.link, {
                      [css.active]: isActive(v),
                    })}
                    onClick={() => handleClick(v)}
                  >
                    {getTitle(v, platform)}
                  </div>
                ))}
              </div>

              {chartData && (
                <div className={css.chart}>
                  <Chart data={chartData as ChartData} />
                </div>
              )}

              {rangedStats && (
                <>
                  <Heading size={3}>{getTitle(key, platform)}</Heading>
                  <div className={css.table}>
                    <TableGrid
                      hideEmptyHeader
                      items={tableItems as TableProps['items']}
                      data={tableData as TableProps['data']}
                      className={css.grid}
                      labelsClassName={css.labels}
                    />
                  </div>
                </>
              )}
            </BoxContent>
          </>
        )}

        {state === 'loading' && <Spinner standalone className={css.loading} />}
      </Box>
    </div>
  );
};
